import React from "react";

import { Chip } from "@mui/material";
import type { ChipProps } from "@mui/material/Chip";
import type { SxProps, Theme } from "@mui/material/styles";

export type RoleChipValue =
  | "User"
  | "Client Admin"
  | "Team Admin"
  | "Developer Admin"
  | "Guest"
  | "Member"
  | "Past Member"
  | "Blocked";

export interface RoleChipProps extends Omit<ChipProps, "label" | "size"> {
  role: RoleChipValue;
  label?: string;
  size?: ChipProps["size"];
  height?: number | string;
  fontSize?: number | string;
  sx?: SxProps<Theme>;
}

const getRoleStyles = (role: RoleChipValue) => {
  switch (role) {
    case "User":
      return {
        backgroundColor: "#e6f0fb",
        color: "#1f5b99",
        borderColor: "#b8d4f2",
      };
    case "Client Admin":
      return {
        backgroundColor: "#fff3e0",
        color: "#ef6c00",
        borderColor: "#ffcc80",
      };
    case "Team Admin":
      return {
        backgroundColor: "#f3e8ff",
        color: "#7b3fb8",
        borderColor: "#d7b7f0",
      };
    case "Developer Admin":
      return {
        backgroundColor: "#ecf3fd",
        color: "#2818b4",
        borderColor: "#bcc0f7",
      };
    case "Guest":
      return {
        backgroundColor: "#f2f4f7",
        color: "#5f6b7a",
        borderColor: "#d9dee5",
      };
    case "Member":
      return {
        backgroundColor: "#e6f6ee",
        color: "#1d7f52",
        borderColor: "#bfe8d1",
      };
    case "Past Member":
      return {
        backgroundColor: "#f2f4f7",
        color: "#5f6b7a",
        borderColor: "#d9dee5",
      };
    case "Blocked":
      return {
        backgroundColor: "#fdecec",
        color: "#b42318",
        borderColor: "#f7c0bc",
      };
    default:
      return {
        backgroundColor: "#f2f4f7",
        color: "#5f6b7a",
        borderColor: "#d9dee5",
      };
  }
};

const RoleChip: React.FC<RoleChipProps> = ({
  role,
  label,
  size = "small",
  height,
  fontSize,
  sx,
  ...chipProps
}) => {
  const roleStyles = getRoleStyles(role);
  const resolvedHeight = height ?? (size === "small" ? 18 : 24);
  const resolvedFontSize = fontSize ?? (size === "small" ? "0.7rem" : "0.8rem");

  return (
    <Chip
      {...chipProps}
      label={label ?? role}
      size={size}
      sx={{
        height: resolvedHeight,
        fontSize: resolvedFontSize,
        fontWeight: 600,
        textTransform: "uppercase",
        border: "1px solid",
        ...roleStyles,
        "& .MuiChip-label": {
          px: 1,
        },
        ...sx,
      }}
    />
  );
};

export default RoleChip;